import { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction, useCallback, useState } from "react";
import { Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { ClaimParams } from "@/api/claimsApi";
import { Search } from "@/components/Search";

type ClaimsSearchProps = {
    claimsQuery: ClaimParams;
    isFetchingClaims: boolean;
    resetPagination: () => void;
    setClaimsQuery: Dispatch<SetStateAction<ClaimParams>>;
};

const ClaimsSearch = ({ claimsQuery, isFetchingClaims, resetPagination, setClaimsQuery }: ClaimsSearchProps) => {
    const { t } = useTranslation("claimsInvoicesOverview");
    const [searchText, setSearchText] = useState<string>("");

    const handleKeyUp = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                resetPagination();
                setClaimsQuery(prev => ({
                    companyId: prev.companyId,
                    queryFilter: { ...prev.queryFilter, query: searchText }
                }));
            }
        },
        [resetPagination, searchText, setClaimsQuery]
    );

    const handleSearchCriteriaChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const originalValue = e.target.value;

        // Check if the first 4 characters are numbers
        // Then remove the "-" for the query
        // eslint-disable-next-line no-magic-numbers
        const firstFourAreNumbers = /^\d{4}/.test(originalValue.slice(0, 4));
        let queryValue = originalValue;
        if (firstFourAreNumbers) {
            queryValue = queryValue.replace(/-.*/, "");
        }

        setSearchText(queryValue);
    }, []);

    const handleSearchClick = useCallback(() => {
        resetPagination();
        setClaimsQuery(prev => ({
            companyId: prev.companyId,
            queryFilter: { ...prev.queryFilter, query: searchText }
        }));
    }, [resetPagination, setClaimsQuery, searchText]);

    const handleClearSearch = useCallback(() => {
        resetPagination();
        setSearchText("");
        setClaimsQuery(prev => ({
            ...prev,
            queryFilter: {
                ...prev.queryFilter,
                query: ""
            }
        }));
    }, [resetPagination, setClaimsQuery]);

    return (
        <Flex w="50%" alignItems="flex-end">
            <Search
                id="claims"
                placeholder={t("claimsSearchPlaceholder")}
                searchText={searchText}
                queryFilter={claimsQuery.queryFilter}
                isFetchingData={isFetchingClaims}
                handleSearchCriteriaChange={handleSearchCriteriaChange}
                handleKeyUp={handleKeyUp}
                handleSearchClick={handleSearchClick}
                handleClearSearch={handleClearSearch}
            />
        </Flex>
    );
};

export default ClaimsSearch;
